import { useCallback, useState } from 'react';

import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { useCpuUsage } from '@/features/monitoring';
import type { ResourceUsageData } from '@/features/monitoring/api';

import { MetricCard } from '@/widgets/monitoring/MetricCard';
import { PodMetricDetailModal } from '@/widgets/monitoring/PodMetricDetailModal';

import styles from './CpuMetric.module.css';

type CpuMetricContentProps = {
  data?: ResourceUsageData;
  isLoading?: boolean;
};

type CpuMetricViewProps = {
  data?: ResourceUsageData;
  isLoading?: boolean;
};

const CpuMetricView = ({ data: cpuData, isLoading: cpuLoading = false }: CpuMetricViewProps) => {
  const [cpuModalOpen, setCpuModalOpen] = useState(false);

  const handleDetailClick = useCallback(() => {
    setCpuModalOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.container}>
      <MetricCard
        title="CPU"
        value={cpuData?.current ?? 0}
        unit={cpuData?.unit ?? 'cores'}
        isLoading={cpuLoading}
        totalCapacity={cpuData?.total}
        percentage={cpuData?.percentage}
        podHistory={
          cpuData?.podHistory.map((pod) => ({
            pod: pod.pod,
            data: pod.data,
            color: pod.color,
          })) ?? []
        }
        onDetailClick={handleDetailClick}
      />

      {cpuData && (
        <PodMetricDetailModal
          open={cpuModalOpen}
          onClose={handleDetailClick}
          title="CPU"
          unit={cpuData.unit}
          podHistory={cpuData.podHistory}
        />
      )}
    </div>
  );
};

const ConnectedContent = () => {
  const { data, isLoading } = useCpuUsage();

  return <CpuMetricView data={data} isLoading={isLoading} />;
};

const Content = ({ data, isLoading }: CpuMetricContentProps) => {
  if (data) {
    return <CpuMetricView data={data} isLoading={isLoading} />;
  }

  return <ConnectedContent />;
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <div className={styles.container}>
      <ErrorFallback title={title} description={description} className={styles.error} {...props} />
    </div>
  );
};

export const CpuMetric = { Content, Error };
